import { useEffect, useRef } from 'react'

// The different dot sizes represent companies; connections follow them as they drift.
const points = [
  [-18, 165, 2], [43, 140, 4.6], [101, 184, 2.4], [154, 145, 3.8], [211, 190, 1.7], [68, 215, 3], [160, 248, 2.5],
  [245, 281, 2.1], [318, 235, 4.7], [398, 265, 2.8], [451, 218, 1.8], [347, 328, 4], [283, 355, 2], [410, 385, 3],
  [-16, 690, 2], [55, 657, 3.2], [115, 709, 1.8], [189, 668, 4.6], [95, 616, 2.4],
  [257, 727, 2.2], [316, 678, 4.8], [389, 711, 2.5], [447, 675, 1.9], [354, 630, 3.7],
]

const connections = [
  [0, 1], [1, 2], [2, 3], [3, 4], [1, 5], [5, 3], [2, 5], [5, 6],
  [7, 8], [8, 9], [9, 10], [8, 11], [11, 9], [7, 12], [12, 11], [11, 13],
  [14, 15], [15, 16], [16, 17], [15, 18], [18, 17],
  [19, 20], [20, 21], [21, 22], [20, 23], [23, 21],
]

export default function LandingNetwork() {
  const dotRefs = useRef([])
  const lineRefs = useRef([])

  useEffect(() => {
    if (typeof window.requestAnimationFrame !== 'function') return undefined

    const motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    let frame = null
    let started = null
    let lastFrame = -Infinity

    const draw = timestamp => {
      if (timestamp - lastFrame >= 32) {
        const seconds = ((timestamp - started) / 1000)
        const positions = points.map(([x, y, radius], index) => {
          const phase = index * 1.73
          const speed = .36 + (index % 5) * .055
          const dx = Math.sin(seconds * speed + phase) * (8 + index % 4 * 2)
          const dy = Math.cos(seconds * speed * .82 + phase) * (7 + index % 3 * 3)
          const pulse = Math.sin(seconds * (.75 + index % 4 * .09) + phase)
          const dot = dotRefs.current[index]
          dot?.setAttribute('cx', x + dx)
          dot?.setAttribute('cy', y + dy)
          dot?.setAttribute('r', radius * (1 + .28 * pulse))
          dot?.setAttribute('opacity', .66 + .34 * (pulse + 1) / 2)
          return [x + dx, y + dy]
        })

        connections.forEach(([from, to], index) => {
          const line = lineRefs.current[index]
          line?.setAttribute('x1', positions[from][0])
          line?.setAttribute('y1', positions[from][1])
          line?.setAttribute('x2', positions[to][0])
          line?.setAttribute('y2', positions[to][1])
        })
        lastFrame = timestamp
      }
      frame = window.requestAnimationFrame(tick)
    }

    const tick = timestamp => {
      if (started === null) started = timestamp
      draw(timestamp)
    }

    const updateMotion = () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      frame = null
      if (!motionPreference?.matches) {
        started = null
        lastFrame = -Infinity
        frame = window.requestAnimationFrame(tick)
      }
    }

    updateMotion()
    motionPreference?.addEventListener?.('change', updateMotion)
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      motionPreference?.removeEventListener?.('change', updateMotion)
    }
  }, [])

  return (
    <svg className="hb-landing-network" viewBox="0 0 430 850" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      {connections.map(([from, to], index) => (
        <line key={`${from}-${to}`} ref={node => { lineRefs.current[index] = node }} x1={points[from][0]} y1={points[from][1]} x2={points[to][0]} y2={points[to][1]} />
      ))}
      {points.map(([x, y, radius], index) => (
        <circle key={index} ref={node => { dotRefs.current[index] = node }} cx={x} cy={y} r={radius} />
      ))}
    </svg>
  )
}
